import IBaseModel from "../base/base-model";
import { ILocation } from "../store/response";
import { IOrderCustomerSatisfaction } from "../customer-satisfaction/response";
import { IProductsOrder } from "./products-order/response";
import type { IOrderCashCheck } from "./cash-check";

// Simple location for location change feature
export interface ISimpleLocation {
  lat: number;
  lng: number;
}

// Browsing store for location change feature
export interface IBrowsingStore {
  id: number;
  name: string;
}

// Trip status enum for order protection
export enum TripStatus {
  PENDING = 1,
  ACTIVE = 2,
  COMPLETED = 3,
  CANCELLED = 4,
}

/**
 * The trip an order belongs to, or belonged to.
 *
 * Unlike the flat `tripId` / `tripNumber` / `tripStatus` fields - which the
 * backend nulls out as soon as the trip ends or is cancelled, and which
 * therefore only ever describe a *current* trip - this object survives the end
 * of the trip and is what every display should read from.
 *
 * When an order passed through several trips the backend picks one: the trip it
 * is currently in, else the trip it was actually delivered in, else the newest
 * standing link, else a withdrawn link.
 */
export interface IOrderTrip {
  id: number;
  number: string;
  status: TripStatus;
  /** false = historical record; also covers an order withdrawn from a live trip. */
  isCurrent: boolean;
  /** Position of the order along the trip route. */
  sequence?: number | null;
  driverId?: number | null;
  driverName?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  /** When *this order* was delivered, not when the trip finished. */
  deliveredAt?: string | null;
  wasSkipped?: boolean;
  /** Set when the order was pulled out of the trip before being delivered in it. */
  removedAt?: string | null;
  removalReason?: string | null;
}

// Delivery mode enum
export enum OrderDeliveryMode {
  LEGACY = 1,
  TRIP_BASED = 2,
}

export type CustomerSatisfactionStatusSummaryKey =
  | "satisfied"
  | "unsatisfied"
  | "no_response"
  | "undefined";

export interface ICustomerSatisfactionStatusSummary {
  value: 1 | 2 | 3 | null;
  key: CustomerSatisfactionStatusSummaryKey | string;
  label: string;
  reason?: { id: number; reason?: string | null } | null;
  handlingMethod?: string | null;
  customerReaction?: string | null;
  determinedAt?: string | null;
}

export type OrderOperationTimingPhaseKey =
  | "storeAcceptance"
  | "preparing"
  | "customerAcceptance"
  | "accountingFinalization"
  | "driverSearchToPickup"
  | "delivery";

export interface IOrderOperationDuration {
  totalSeconds: number;
  minutes: number;
  seconds: number;
}

export interface IOrderOperationTimingPhase {
  key: OrderOperationTimingPhaseKey | string;
  startedAt?: string | null;
  endedAt?: string | null;
  durationSeconds?: number | null;
  duration?: IOrderOperationDuration | null;
  elapsedSeconds?: number | null;
  elapsedDuration?: IOrderOperationDuration | null;
  effectiveSeconds?: number | null;
  effectiveDuration?: IOrderOperationDuration | null;
  isActive?: boolean;
}

export interface IOrderOperationTimingsDataQuality {
  missingCycleStart?: boolean;
  missingCompletedAt?: boolean;
  repeatedStatuses?: Array<string | number | Record<string, any>>;
  stageSumGreaterThanTotal?: boolean;
  [key: string]: any;
}

export interface IOrderOperationTimings {
  cycleStartedAt?: string | null;
  completedAt?: string | null;
  finishedAt?: string | null;
  measuredUntilAt?: string | null;
  currentPhase?: OrderOperationTimingPhaseKey | string | null;
  currentPhaseElapsedSeconds?: number | null;
  currentPhaseElapsedDuration?: IOrderOperationDuration | null;
  totalCycleSeconds?: number | null;
  totalCycleDuration?: IOrderOperationDuration | null;
  totalCompletedSeconds?: number | null;
  totalCompletedDuration?: IOrderOperationDuration | null;
  storeAcceptanceSeconds?: number | null;
  storeAcceptanceDuration?: IOrderOperationDuration | null;
  preparingSeconds?: number | null;
  preparingDuration?: IOrderOperationDuration | null;
  customerAcceptanceSeconds?: number | null;
  customerAcceptanceDuration?: IOrderOperationDuration | null;
  accountingFinalizationSeconds?: number | null;
  accountingFinalizationDuration?: IOrderOperationDuration | null;
  driverSearchToPickupSeconds?: number | null;
  driverSearchToPickupDuration?: IOrderOperationDuration | null;
  deliverySeconds?: number | null;
  deliveryDuration?: IOrderOperationDuration | null;
  unattributedSeconds?: number | null;
  unattributedDuration?: IOrderOperationDuration | null;
  stageCoverageRate?: number | null;
  stageSumGreaterThanTotal?: boolean;
  isCompleted?: boolean;
  isTerminal?: boolean;
  phases?: Partial<
    Record<OrderOperationTimingPhaseKey, IOrderOperationTimingPhase>
  > &
    Record<string, IOrderOperationTimingPhase | undefined>;
  dataQuality?: IOrderOperationTimingsDataQuality;
}

export interface IOrderShort {
  id: number;
  status: number;
  createdAt: string;
  store: any;
  // The trip to display - survives the end of the trip, unlike the fields below
  trip?: IOrderTrip | null;
  // Trip information fields - current trip only, null once the trip ends.
  // Keep driving order-protection logic off these; never display them.
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
}

export interface IOrder {
  id: number;
  client?: Client | null;
  driver: any;
  type: number;
  store: any;
  investor: any;
  status: number;
  finalPrice: number;
  isEmergency: boolean;
  isRecipe: boolean;
  isExpressDelivery: boolean;
  isSubscriptionCoupon?: boolean;
  client_note: any;
  note: string;
  createdAt: string;
  orderSchedulingType: number;
  paymentReceivedBy?: User;
  paymentReceivedAt?: string;
  accountingFinishedBy?: User;
  accountingFinishedAt?: string | null;
  location?: ILocation;
  // The trip to display - survives the end of the trip, unlike the fields below
  trip?: IOrderTrip | null;
  // Trip information fields - current trip only, null once the trip ends.
  // Keep driving order-protection logic off these; never display them.
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
  customerSatisfaction?: IOrderCustomerSatisfaction | null;
  customerSatisfactionStatus?: ICustomerSatisfactionStatusSummary | null;
  operationTimings?: IOrderOperationTimings | null;
}

export interface IRatedOrder {
  id: number;
  client?: Client | null;
  type: number;
  status: number;
  finalPrice: number;
  totalPriceOnClient: number;
  rating: any;
  clientRateNote: string;
  createdAt: string;
  finishDatetime: string;
  // The trip to display - survives the end of the trip, unlike the fields below
  trip?: IOrderTrip | null;
  // Trip information fields - current trip only, null once the trip ends.
  // Keep driving order-protection logic off these; never display them.
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
}

export interface IOrderDetails extends IBaseModel {
  id: number;
  number: string;
  client?: Client | null;
  driver: any;
  type: number;
  store: any;
  investor: any;
  status: number;
  finalPrice: number;
  isEmergency: boolean;
  orderBroadcastArea: boolean;
  orderBroadcastCity: boolean;
  productBroadcastArea: boolean;
  productBroadcastCity: boolean;
  isRecipe: boolean;
  StoreHaveRecipe: any;
  recipes: any[];
  recipeAsText: any;
  notes: any[];
  clientNote: any;
  distance: any;
  totalPrice: number;
  totalPriceOnClient: number;
  productYield: number;
  priceDeliveryService: number;
  priceDeliveryServiceOnClient: number;
  priceDeliveryYield: number;
  discountCode: any;
  discountCodeValueForDelivery: number;
  discountCodeValueForProduct: number;
  rating: any;
  contact: boolean;
  ringingBell: boolean;
  clientNoteDelivery: any;
  note: string;
  orderProducts: IProductsOrder[];
  selectedStore: boolean;
  reasonCancel: any;
  clientRateNote: string;
  finishDatetime: Date;
  startDatetimeDriver: Date;
  clientAddress: string;
  orderSchedulingType: number;
  scheduleTime: Date;
  location: ILocation;
  isExpressDelivery: boolean;
  isSubscriptionCoupon?: boolean;
  quantityProcessed: boolean;
  paymentReceivedBy?: User;
  paymentReceivedAt?: string;
  accountingFinishedBy?: User;
  accountingFinishedAt?: string | null;
  accountingFinishNote?: string | null;
  canMarkAccountingFinished?: boolean;
  customerSatisfaction?: IOrderCustomerSatisfaction | null;
  customerSatisfactionStatus?: ICustomerSatisfactionStatusSummary | null;
  operationTimings?: IOrderOperationTimings | null;
  // The trip to display - survives the end of the trip, unlike the fields below
  trip?: IOrderTrip | null;
  // Trip protection fields - current trip only, null once the trip ends.
  // Keep driving order-protection logic off these; never display them.
  tripId?: number | null;
  tripNumber?: string | null;
  tripStatus?: TripStatus | null;
  // Delivery mode
  deliveryMode?: OrderDeliveryMode;
  // Location change fields
  locationChanged?: boolean;
  currentLocation?: ISimpleLocation | null;
  browsingStore?: IBrowsingStore | null;
  deliveryStore?: IBrowsingStore | null;
  browsingTotalPrice?: number | null;
  deliveryTotalPrice?: number | null;
  priceDifference?: number | null;
  cashCheck?: IOrderCashCheck;
}

export interface Client {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  mobile?: string | null;
  cleanMobile?: string | null;
  acceptedOrdersCount?: number;
  completedOrdersCount?: number;
  lastCompletedOrderDate?: string;
}

export interface User {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}
